/**
 * useUserMatches - Hook for fetching user's assigned matches
 *
 * Features:
 * - Fetch all matches where user is assigned (photographer or editor)
 * - Detect live matches (current time between start and end)
 * - Refresh periodically for live detection
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchAllMatches, fetchMatches as apiFetchMatches } from '../api';

export default function useUserMatches() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMatches = useCallback(async () => {
    try {
      // Fetch matches from API - uses company filter by default.
      // All pages: with >50 matches the paginated first page can drop the
      // very match the editor is working, breaking live detection.
      const data = await fetchAllMatches();
      setMatches(data || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch matches:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLiveMatches = useCallback(async () => {
    try {
      const data = await apiFetchMatches({ live: true });
      return data || [];
    } catch (err) {
      console.error('Failed to fetch live matches:', err);
      return [];
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchMatches();
  }, [fetchMatches]);

  // Compute live matches based on current time
  const liveMatches = useMemo(() => {
    const now = new Date();
    return matches.filter((match) => {
      const start = new Date(match.start_time);
      const end = new Date(match.end_time);
      return now >= start && now <= end;
    });
  }, [matches]);

  // Get live match IDs as a Set for quick lookup
  const liveMatchIds = useMemo(() => {
    return new Set(liveMatches.map((m) => m.id));
  }, [liveMatches]);

  // Check if a specific match is live
  const isMatchLive = useCallback(
    (matchId) => {
      return liveMatchIds.has(matchId);
    },
    [liveMatchIds]
  );

  return {
    matches,
    liveMatches,
    loading,
    error,
    isMatchLive,
    refetch: fetchMatches,
    fetchLiveMatches,
  };
}
